import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { Grid } from '../components/common/TypedGrid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Image as ImageIcon,
  DragIndicator as DragIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  Link as LinkIcon,
  OpenInNew as ExternalLinkIcon,
} from '@mui/icons-material';
import adminService, { BannerAd } from '../services/adminService';
import { ServiceSegment } from '../types/services';
import { PageHeader, ResponsiveTable, StatCard, StatusChip } from '../components/common';

interface BannerFormData {
  id?: string;
  imageUrl: string;
  bannerText: string;
  linkType: 'external' | 'internal';
  externalLink?: string;
  internalLink?: string;
  displayOrder: number;
  isActive: boolean;
}

const BannersAds: React.FC = () => {
  const [banners, setBanners] = useState<BannerAd[]>([]);
  const [serviceSegments, setServiceSegments] = useState<ServiceSegment[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<BannerAd | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState<BannerAd | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [formData, setFormData] = useState<BannerFormData>({
    imageUrl: '',
    bannerText: '',
    linkType: 'external',
    externalLink: '',
    internalLink: '',
    displayOrder: 1,
    isActive: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [bannersData, segmentsData] = await Promise.all([
        adminService.getAllBannerAds(),
        adminService.getAllSegments(),
      ]);
      setBanners(bannersData);
      setServiceSegments(segmentsData);
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = (banner?: BannerAd) => {
    if (banner) {
      setEditingBanner(banner);
      setFormData({
        id: banner.id,
        imageUrl: banner.imageUrl,
        bannerText: banner.bannerText,
        linkType: banner.linkType,
        externalLink: banner.externalLink || '',
        internalLink: banner.internalLink || '',
        displayOrder: banner.displayOrder,
        isActive: banner.isActive,
      });
    } else {
      setEditingBanner(null);
      setFormData({
        imageUrl: '',
        bannerText: '',
        linkType: 'external',
        externalLink: '',
        internalLink: '',
        displayOrder: banners.length + 1,
        isActive: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingBanner(null);
    setFormData({
      imageUrl: '',
      bannerText: '',
      linkType: 'external',
      externalLink: '',
      internalLink: '',
      displayOrder: 1,
      isActive: true,
    });
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showSnackbar('Please select an image file', 'error');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showSnackbar('Image size must be less than 5MB', 'error');
      return;
    }

    setUploading(true);
    try {
      const imageUrl = await adminService.uploadBannerImage(file);
      setFormData({ ...formData, imageUrl });
      showSnackbar('Image uploaded successfully', 'success');
    } catch (error) {
      console.error('Error uploading image:', error);
      showSnackbar('Error uploading image', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.imageUrl) {
      showSnackbar('Please upload an image', 'error');
      return;
    }
    if (!formData.bannerText.trim()) {
      showSnackbar('Please enter banner text', 'error');
      return;
    }
    if (formData.linkType === 'external' && !formData.externalLink?.trim()) {
      showSnackbar('Please enter external link URL', 'error');
      return;
    }
    if (formData.linkType === 'internal' && !formData.internalLink?.trim()) {
      showSnackbar('Please select a service segment', 'error');
      return;
    }

    setSaving(true);
    try {
      const bannerData: any = {
        imageUrl: formData.imageUrl,
        bannerText: formData.bannerText.trim(),
        linkType: formData.linkType,
        displayOrder: formData.displayOrder,
        isActive: formData.isActive,
      };

      // Only add link fields if they have values to avoid undefined in Firestore
      if (formData.linkType === 'external' && formData.externalLink?.trim()) {
        bannerData.externalLink = formData.externalLink.trim();
      }
      if (formData.linkType === 'internal' && formData.internalLink?.trim()) {
        bannerData.internalLink = formData.internalLink.trim();
      }

      if (editingBanner) {
        await adminService.updateBannerAd(editingBanner.id, bannerData);
        showSnackbar('Banner updated successfully', 'success');
      } else {
        await adminService.createBannerAd(bannerData);
        showSnackbar('Banner created successfully', 'success');
      }

      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Error saving banner:', error);
      showSnackbar('Error saving banner', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (banner: BannerAd) => {
    setBannerToDelete(banner);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bannerToDelete) return;

    try {
      await adminService.deleteBannerAd(bannerToDelete.id);
      showSnackbar('Banner deleted successfully', 'success');
      setDeleteConfirmOpen(false);
      setBannerToDelete(null);
      loadData();
    } catch (error) {
      console.error('Error deleting banner:', error);
      showSnackbar('Error deleting banner', 'error');
    }
  };

  const getInternalLinkDisplay = (internalLink: string) => {
    const segment = serviceSegments.find(s => s.id === internalLink);
    return segment ? segment.name : internalLink;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Banners & Ads"
        subtitle="Manage promotional banners and advertisements"
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Add New Banner
          </Button>
        }
      />

      <Grid container spacing={{ xs: 1.5, md: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard label="Total Banners" value={banners.length} icon={<ImageIcon />} tone="primary" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Active Banners"
            value={banners.filter((b) => b.isActive).length}
            icon={<ViewIcon />}
            tone="success"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="External Links"
            value={banners.filter((b) => b.linkType === 'external').length}
            icon={<ExternalLinkIcon />}
            tone="warning"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Internal Links"
            value={banners.filter((b) => b.linkType === 'internal').length}
            icon={<LinkIcon />}
            tone="info"
          />
        </Grid>
      </Grid>

      {/* Banners Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            All Banners
          </Typography>
          <ResponsiveTable
            columns={[
              {
                key: 'order',
                header: 'Order',
                width: 90,
                render: (banner: any) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DragIcon color="action" />
                    {banner.displayOrder}
                  </Box>
                ),
              },
              {
                key: 'image',
                header: 'Image',
                render: (banner: any) => (
                  <Avatar
                    src={banner.imageUrl}
                    variant="rounded"
                    alt=""
                    sx={{ width: 64, height: 36 }}
                  >
                    <ImageIcon />
                  </Avatar>
                ),
              },
              {
                key: 'text',
                header: 'Banner Text',
                mobile: 'title',
                render: (banner: any) => (
                  <Typography variant="body2" sx={{ maxWidth: { md: 200 } }}>
                    {banner.bannerText}
                  </Typography>
                ),
              },
              {
                key: 'linkType',
                header: 'Link Type',
                render: (banner: any) => (
                  <Chip
                    label={banner.linkType}
                    color={banner.linkType === 'external' ? 'warning' : 'info'}
                    size="small"
                  />
                ),
              },
              {
                key: 'link',
                header: 'Link',
                render: (banner: any) => (
                  <Typography
                    variant="body2"
                    sx={{ maxWidth: { md: 150 }, wordBreak: 'break-all' }}
                  >
                    {banner.linkType === 'external'
                      ? banner.externalLink
                      : getInternalLinkDisplay(banner.internalLink || '')}
                  </Typography>
                ),
              },
              {
                key: 'status',
                header: 'Status',
                render: (banner: any) => (
                  <StatusChip status={banner.isActive ? 'active' : 'inactive'} />
                ),
              },
              {
                key: 'actions',
                header: 'Actions',
                align: 'right',
                mobile: 'actions',
                render: (banner: any) => (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(banner)}
                      aria-label="Edit banner"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(banner)}
                      color="error"
                      aria-label="Delete banner"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ),
              },
            ]}
            rows={[...banners].sort((a, b) => a.displayOrder - b.displayOrder)}
            rowKey={(banner) => banner.id}
            aria-label="Banners"
            emptyTitle="No banners yet"
            emptyDescription="Create your first banner to feature it in the app."
          />
        </CardContent>
      </Card>

      {/* Banner Form Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingBanner ? 'Edit Banner' : 'Add New Banner'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Image Upload */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Banner Image *
              </Typography>
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  mb: 2,
                  '&:hover': { borderColor: 'primary.main' },
                }}
                onClick={() => document.getElementById('image-upload')?.click()}
              >
                {formData.imageUrl ? (
                  <Box>
                    <img
                      src={formData.imageUrl}
                      alt="Banner preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: 200,
                        borderRadius: 8,
                      }}
                    />
                    <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                      Click to change image
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    {uploading ? (
                      <CircularProgress />
                    ) : (
                      <>
                        <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          Click to upload banner image
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Supported formats: JPG, PNG, GIF (max 5MB)
                        </Typography>
                      </>
                    )}
                  </Box>
                )}
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
              </Box>
            </Box>

            {/* Banner Text */}
            <TextField
              fullWidth
              label="Banner Text"
              value={formData.bannerText}
              onChange={(e) => setFormData({ ...formData, bannerText: e.target.value })}
              required
              sx={{ mb: 3 }}
              helperText="Text to display on the banner (optional but recommended)"
            />

            {/* Link Type */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Link Type *</InputLabel>
              <Select
                value={formData.linkType}
                label="Link Type *"
                onChange={(e) => setFormData({ 
                  ...formData, 
                  linkType: e.target.value as 'external' | 'internal',
                  externalLink: '',
                  internalLink: '',
                })}
              >
                <MenuItem value="external">External URL</MenuItem>
                <MenuItem value="internal">Internal Service Segment</MenuItem>
              </Select>
            </FormControl>

            {/* External Link */}
            {formData.linkType === 'external' && (
              <TextField
                fullWidth
                label="External Link URL"
                value={formData.externalLink}
                onChange={(e) => setFormData({ ...formData, externalLink: e.target.value })}
                required
                sx={{ mb: 3 }}
                placeholder="https://example.com"
                helperText="Enter the full URL including https://"
              />
            )}

            {/* Internal Link */}
            {formData.linkType === 'internal' && (
              <Autocomplete
                options={serviceSegments}
                getOptionLabel={(option) => option.name}
                value={serviceSegments.find(s => s.id === formData.internalLink) || null}
                onChange={(_, newValue) => {
                  setFormData({ ...formData, internalLink: newValue?.id || '' });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Service Segment"
                    required
                    helperText="Choose which service segment this banner should link to"
                  />
                )}
                sx={{ mb: 3 }}
              />
            )}

            {/* Display Order */}
            <TextField
              fullWidth
              label="Display Order"
              type="number"
              value={formData.displayOrder}
              onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 1 })}
              required
              sx={{ mb: 3 }}
              helperText="Lower numbers appear first (1 = first, 2 = second, etc.)"
              inputProps={{ min: 1 }}
            />

            {/* Status */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active (Banner will be visible to users)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : (editingBanner ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Banner</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this banner? This action cannot be undone.
          </Typography>
          {bannerToDelete && (
            <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Banner: {bannerToDelete.bannerText}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BannersAds;
